import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";
import { ItemSizesProps, ItemSizesFilterType } from "../../types/itemSize";

export interface ItemSizesState {
  itemSizes: ItemSizesProps[];
  totalItemSizes: number;
  filter: ItemSizesFilterType;
  isItemSizeModalOpen: boolean;
  editItemSize: ItemSizesProps | null;
  loading: boolean;
  error: string | null;
}

const initialState: ItemSizesState = {
  itemSizes: [],
  totalItemSizes: 0,
  filter: {
    search: null,
    sortBy: "id",
    sortOrder: "desc",
  },
  isItemSizeModalOpen: false,
  editItemSize: null,
  loading: false,
  error: null,
};

type FetchItemSizesListDataProps = {
  page: number;
  size?: number;
  search?: string | null;
  sortBy?: string;
  sortOrder?: string;
};

export const fetchItemSizesListData = createAsyncThunk(
  "itemSizes/fetchList",
  async (req: FetchItemSizesListDataProps) => {
    let response: any;
    await GET(`/v1/item-sizes`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const itemSizesSlice = createSlice({
  name: "itemSizes",
  initialState,
  reducers: {
    toggleItemSizeModal: (state, action: PayloadAction<boolean>) => {
      state.isItemSizeModalOpen = action.payload;
    },
    setEditItemSize: (state, action: PayloadAction<ItemSizesProps | null>) => {
      state.editItemSize = action.payload;
    },
    setItemSizesFilter: (state, action: PayloadAction<Partial<ItemSizesFilterType>>) => {
      state.filter = { ...state.filter, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchItemSizesListData.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchItemSizesListData.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload?.data?.status === "success") {
        state.itemSizes = action.payload.data.data.records;
        state.totalItemSizes = action.payload.data.data.total_records;
        state.error = null;
      } else {
        state.itemSizes = [];
        state.error =
          action?.payload?.data?.message ||
          "Something went wrong. Please try again.";
      }
    });
    builder.addCase(fetchItemSizesListData.rejected, (state, action: any) => {
      state.itemSizes = [];
      state.loading = false;
      state.error =
        action?.payload?.data?.message ||
        "Something went wrong. Please try again.";
    });
  },
});

export const { toggleItemSizeModal, setEditItemSize, setItemSizesFilter } = itemSizesSlice.actions;

export const getItemSizes = (state: RootState) => state.itemSizes.itemSizes;
export const getTotalItemSizes = (state: RootState) => state.itemSizes.totalItemSizes;
export const getItemSizesFilter = (state: RootState) => state.itemSizes.filter;
export const getIsItemSizeModalOpen = (state: RootState) => state.itemSizes.isItemSizeModalOpen;
export const getEditItemSize = (state: RootState) => state.itemSizes.editItemSize;
export const getItemSizesLoading = (state: RootState) => state.itemSizes.loading;

export default itemSizesSlice.reducer;
